/**
 * SDX SPA transport primitives.
 *
 * Single source of truth for the `X-SDX-*` HTTP header contract the
 * server consumes and for the fetch helpers that the deferred / lazy
 * DataProvider prop loaders share across every framework adapter.
 * Both the header names and the follow-up fetch shape used to be
 * duplicated inline in six hook files and two unrelated consumers,
 * which meant adding a new signal (e.g. a new header name) required
 * touching ten files and any typo silently desynchronised the
 * client from the server.
 *
 * This module centralises both:
 *
 * - `SDX_HEADERS` is a `const` map of typed header-name constants.
 *   Every call site that builds an SPA fetch imports from here and
 *   lets TypeScript catch typos and missing headers. `fetch-page.ts`
 *   and framework DataProvider contexts use the constants; the
 *   DataProvider hook fetchers use them via the helper below.
 *
 * - `fetchDataProviderProps()` is the shared helper every framework's
 *   useDeferredProps / useLazyProp calls. It builds the header stack,
 *   attaches the CSRF token, parses the JSON envelope, and returns
 *   `null` on any non-ok or transport failure so each hook can
 *   uniformly degrade to "render without the missing prop". No
 *   framework-specific state lives here; the hooks supply their own
 *   reactivity around the resolved value.
 */

import { getCsrfToken } from '@sdx/security';

/**
 * Typed HTTP header names used by the SDX server/client SPA contract.
 *
 * `as const` so every entry widens to its literal value, letting
 * callers write `headers[SDX_HEADERS.NAVIGATE] = '1'` with no magic
 * string drift between client and server.
 */
export const SDX_HEADERS = {
  /** Unconditional marker on every SDX SPA / DataProvider fetch. */
  NAVIGATE: 'X-SDX-Navigate',
  /** Requested render scope: `content`, `page`, or `full`. */
  SCOPE: 'X-SDX-Scope',
  /** Comma-separated list of deferred prop keys to resolve. */
  DEFERRED: 'X-SDX-Deferred',
  /** Single prop key the client wants resolved lazily. */
  LAZY: 'X-SDX-Lazy',
  /** Comma-separated list of prop keys the client considers stale. */
  STALE: 'X-SDX-Stale',
  /** `tag:checksum,...` manifest for partial-response bookkeeping. */
  TAGS: 'X-SDX-Tags',
  /** Client-tracked route name, for scope auto-upgrade detection. */
  ROUTE: 'X-SDX-Route',
  /** Client-tracked menu hash, for scope auto-upgrade detection. */
  MENU_HASH: 'X-SDX-Menu-Hash',
  /** `region:checksum,...` manifest sent on every partial fetch. */
  REGION_TAGS: 'X-SDX-Region-Tags',
  /** Response header: `1` if the body is a partial JSON envelope. */
  PARTIAL: 'X-SDX-Partial',
  /**
   * Compressed `ajax_page_state[libraries]` manifest for the
   * current document. Matches the value Drupal core embeds in
   * `drupalSettings.ajaxPageState.libraries`.
   *
   * Sent as a header (not a query parameter) so it survives
   * same-origin redirects - notably the 303 Drupal form
   * handlers emit after a successful POST. Without redirect-
   * safe propagation, the redirected GET reaches Drupal with
   * no library manifest, the response re-delivers the full
   * library bundle (including `core/drupal`), and evaluating
   * that bundle resets `window.Drupal` and wipes
   * `Drupal.sdxNavigation`. `sdx_router`'s
   * `SdxAjaxLibrariesHeader` stack middleware lifts this header
   * into `$request->query['ajax_page_state']` so core's own
   * `AjaxPageState` middleware processes it uniformly.
   */
  AJAX_LIBRARIES: 'X-SDX-Ajax-Libraries',
  /** Drupal CSRF token header. */
  CSRF: 'X-CSRF-Token',
} as const;

/**
 * Strips userinfo credentials from a URL so it can be passed to
 * `fetch()`.
 *
 * When a page is opened through a credentialed URL
 * (`https://user:pass@host/`), the browser resolves `location.href`
 * and every anchor's `.href` with the userinfo still embedded, and
 * the Fetch standard forbids requests to credentialed URLs -
 * `fetch()` throws a TypeError before any request is made. Stripping
 * loses nothing: `credentials: 'same-origin'` re-attaches the
 * browser's cached Authorization header on its own.
 *
 * Returns the input unchanged when it carries no credentials (so
 * relative URLs stay relative) or when it does not parse - callers'
 * own error paths handle unparseable input.
 */
export function toFetchableUrl(url: string): string {
  try {
    const parsed = new URL(url, location.origin);
    if (parsed.username !== '' || parsed.password !== '') {
      parsed.username = '';
      parsed.password = '';
      return parsed.href;
    }
  }
  catch {
    // Fall through to returning the input verbatim.
  }
  return url;
}

/**
 * Shape of the JSON body returned by a deferred / lazy prop fetch.
 */
export interface DataProviderPartialResponse<T = Record<string, unknown>> {
  /** The resolved prop values, keyed by prop name. */
  props: T;
  /** Server-side signal that the envelope is partial. Always `true` here. */
  _partial?: boolean;
}

/**
 * Mode discriminator for {@link fetchDataProviderProps}.
 *
 * `deferred` fetches a list of keys in one round trip and is driven
 * by `useDeferredProps` automatically after first paint; `lazy`
 * fetches a single key on user demand from `useLazyProp`.
 */
export type DataProviderFetchMode =
  | { readonly kind: 'deferred'; readonly keys: readonly string[] }
  | { readonly kind: 'lazy'; readonly key: string };

/**
 * Fetches deferred or lazy DataProvider props for the given URL.
 *
 * The caller supplies the target URL (typically `window.location.href`
 * so the active route's DataProvider handles the request) and the
 * fetch mode. On success the parsed response body is returned; on any
 * non-ok status or transport error the result is `null`, so framework
 * hooks can uniformly treat failure as "the component renders without
 * the missing prop" without duplicating try/catch plumbing.
 *
 * Implementation note: we deliberately do not throw on failure. Every
 * hook consumer already translates a failed follow-up fetch into
 * "data stays null / keep current props", so propagating the error
 * forces each hook to re-wrap it in the same way. Returning `null`
 * bakes the convention in one place.
 */
export async function fetchDataProviderProps<T = Record<string, unknown>>(
  url: string,
  mode: DataProviderFetchMode,
): Promise<DataProviderPartialResponse<T> | null> {
  const headers: Record<string, string> = {
    [SDX_HEADERS.NAVIGATE]: '1',
  };
  if (mode.kind === 'deferred') {
    if (mode.keys.length === 0) return null;
    headers[SDX_HEADERS.DEFERRED] = mode.keys.join(',');
  }
  else {
    headers[SDX_HEADERS.LAZY] = mode.key;
  }

  try {
    headers[SDX_HEADERS.CSRF] = await getCsrfToken();
    const response = await fetch(toFetchableUrl(url), {
      credentials: 'same-origin',
      headers,
    });
    if (!response.ok) return null;
    const body = await response.json();
    if (!body || typeof body !== 'object' || !('props' in body)) return null;
    return body as DataProviderPartialResponse<T>;
  }
  catch {
    return null;
  }
}

/**
 * Makes an authenticated JSON API call.
 *
 * Attaches a fresh CSRF token, sends the body as JSON, parses the
 * response as JSON, and throws on any non-ok status. The response
 * shape is generic: callers pass the expected type via
 * `apiCall<ResponseShape>(...)` so the result is typed without an
 * `as any` cast at the call site. Defaults to `unknown` when no type
 * is supplied, forcing the caller to narrow before use.
 *
 * Unlike `fetchDataProviderProps`, this helper THROWS on failure.
 * It is used by mutation endpoints (save, reorder, toggle, ...)
 * where the caller needs to know whether the write succeeded and
 * surface an error to the user if not; silent `null` would hide
 * data-integrity problems. The error message prefers a server-side
 * `error` field over the raw HTTP status so Drupal's form
 * validation messages reach the UI verbatim.
 */
export async function apiCall<T = unknown>(
  url: string,
  method: string = 'POST',
  body?: object,
): Promise<T> {
  const token = await getCsrfToken();
  const res = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      [SDX_HEADERS.CSRF]: token,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch<{ error?: string }>(() => ({ error: `HTTP ${res.status}` }));
    throw new Error(err?.error || `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}
